import React, { useState, useEffect } from 'react';
import { User } from '../../types/auth';
import { CreateUserDto, UpdateUserDto } from '../../services/userService';
import {
  Button,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  Select,
  Switch,
} from '../ui';

interface UserFormProps {
  user?: User;
  onSubmit: (data: CreateUserDto | UpdateUserDto) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  /** Save error from the parent, shown above the actions */
  error?: string | null;
}

type Role = 'super_admin' | 'admin' | 'top_owner' | 'user';

export const USER_FORM_TITLE_ID = 'user-form-title';

const roleOptions: Array<{ value: Role; label: string; description: string }> = [
  { value: 'user', label: 'User', description: 'Limited access' },
  { value: 'top_owner', label: 'Top Owner', description: 'Approves content in scope' },
  { value: 'admin', label: 'Admin', description: 'Manages users and settings' },
  { value: 'super_admin', label: 'Super Admin', description: 'Full access' },
];

/**
 * Add / edit user form. Renders dialog header, body and footer, so place it
 * directly inside a kit `Dialog`.
 */
export const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading = false,
  error,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user' as Role,
    firstName: '',
    lastName: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        password: '',
        confirmPassword: '',
        role: user.role,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        isActive: user.isActive,
      });
    }
  }, [user]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation (only for new users or when password is provided)
    if (!user && !formData.password) {
      newErrors.password = 'Password is required for new users';
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    // Confirm password validation
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (user) {
        // Update user - only include changed fields
        const updateData: UpdateUserDto = {
          email: formData.email !== user.email ? formData.email : undefined,
          role: formData.role !== user.role ? formData.role : undefined,
          firstName: formData.firstName !== (user.firstName || '') ? formData.firstName || undefined : undefined,
          lastName: formData.lastName !== (user.lastName || '') ? formData.lastName || undefined : undefined,
          isActive: formData.isActive !== user.isActive ? formData.isActive : undefined,
        };

        // Remove undefined values
        const cleanUpdateData = Object.fromEntries(
          Object.entries(updateData).filter(([_, value]) => value !== undefined)
        ) as UpdateUserDto;

        await onSubmit(cleanUpdateData);
      } else {
        // Create new user
        const createData: CreateUserDto = {
          email: formData.email,
          password: formData.password,
          role: formData.role,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
        };

        await onSubmit(createData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const setField = (name: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
      <DialogHeader id={USER_FORM_TITLE_ID} title={user ? 'Edit user' : 'New user'} onClose={onCancel} />

      <DialogBody className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" htmlFor="firstName">
            <Input
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              autoComplete="off"
            />
          </Field>
          <Field label="Last name" htmlFor="lastName">
            <Input
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              autoComplete="off"
            />
          </Field>
        </div>

        <Field label="Email" htmlFor="email" required error={errors.email}>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            aria-invalid={!!errors.email}
            autoComplete="off"
            inputMode="email"
          />
        </Field>

        <Field
          label="Role"
          htmlFor="role"
          required
          error={errors.role}
          hint={roleOptions.find(option => option.value === formData.role)?.description}
        >
          <Select id="role" name="role" value={formData.role} onChange={handleChange}>
            {roleOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label={user ? 'New password' : 'Password'}
            htmlFor="password"
            required={!user}
            error={errors.password}
            hint={user ? 'Blank keeps current' : 'Min. 8 characters'}
          >
            <Input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              aria-invalid={!!errors.password}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirm" htmlFor="confirmPassword" error={errors.confirmPassword}>
            <Input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              aria-invalid={!!errors.confirmPassword}
              autoComplete="new-password"
            />
          </Field>
        </div>

        {/* Active status (only when editing) */}
        {user && (
          <div className="flex items-center justify-between rounded-xl bg-neutral-50 px-3.5 py-2.5">
            <span className="text-[13px] text-ink">Active</span>
            <Switch
              checked={formData.isActive}
              onChange={checked => setField('isActive', checked)}
              label="User is active"
            />
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
          </p>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="secondary" onClick={onCancel} disabled={isLoading} className="flex-1 sm:flex-none">
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={isLoading} className="flex-1 sm:flex-none">
          {user ? 'Save' : 'Add user'}
        </Button>
      </DialogFooter>
    </form>
  );
};
